
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import axios from 'axios';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';
import { InputCheckbox } from 'component-library/index';
import './ApplicationEsign.css';
import SessionStorage from '../../utils/SessionStorage';
import { TPrequalResponse } from '../../types/responses/PrequalResponse';
import { AcquisitionFlow, clearSessionAndRedirectToDataEntryPage, clearSessionStorageVariablesForAcquisitionFlow, getAcquisitionFlow, getAppData, submitConsent } from '../../utils/acquisition-utils';
import { cleanInternalLinkUrl, isAuthorMode } from '../../utils/cob-utils';
import { Row, Col, Container } from '../../importCL';


interface IApplicationEsignProps extends MappedComponentProperties {
    topBorderAdded?: boolean;
    bottomBorderAdded?: boolean;
}

interface IEsignContent {
    availableFlows?: {[key in AcquisitionFlow | 'all']?: boolean},
    instructionText?: string,
    fieldsetTitle?: string,
    disclosureText?: string,
    agreeText?: string,
    experienceFragmentExists?: boolean,
    error?: string,
    version?: string,
}

export const ApplicationEsignEditConfig = {
    emptyLabel: 'Application Esign',
    isEmpty: (props: PropsWithChildren<IApplicationEsignProps>) => {
        return !props || !props.topBorderAdded;
    },
};


const ApplicationEsign = (props: PropsWithChildren<IApplicationEsignProps>): JSX.Element => {
    const [agree, setAgree] = useState<boolean>(false);
    const [esignContent, setEsignContent] = useState<IEsignContent>();
    const [instructionText, setInstructionText] = useState('');
    const isProduction = window.location.hostname.includes('creditone');
    const { setIsEsignAgreed, setIsEsignRequired, setIsEsignShowing,
        isEsignShowing, isEsignRequired, setIsEsignLinkValid, setEsignVersion,
        isApplicationInfoRecieved} = useAcquisitionContext();
    const [hasWarned, setHasWarned] = useState(false);

    const handleRedirect = () => {
        if (getAcquisitionFlow() === AcquisitionFlow.SkipPQ) {
            clearSessionStorageVariablesForAcquisitionFlow();
            window.location.assign(cleanInternalLinkUrl('/offer-unavailable'));
        } else {
            clearSessionAndRedirectToDataEntryPage('noTncError');
        }
    };

    const areAllEsignShown = (esignContent: IEsignContent) : boolean => {
        const {
            disclosureText, 
            instructionText, 
            fieldsetTitle, 
            availableFlows,
        } = esignContent;

        if (disclosureText 
            && instructionText
            && fieldsetTitle
            && (
                availableFlows?.all === true
                || availableFlows![getAcquisitionFlow()] === true
            )
        ) {
            return true;
        }
        return false;
    };

    const handleEsign = async () => {
        try {
            const programCode = SessionStorage.get('selectedProgramCode');
            let isSecuredCard: boolean;

            if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
                // TODO: Do a full type for ApplicationOfferResponse in sessionstorage
                const applicationOfferResponse = SessionStorage.tryGetObject('applicationOfferResponse', {} as any); // Any used to prevent nested field errors from typescript.
                isSecuredCard = applicationOfferResponse?.offerSecuredCard ?? false;
            } else {
                const prequalResponse: TPrequalResponse = await SessionStorage.tryGetCipher('prequalResponse', {} as any); // Any used to prevent nested field errors from typescript.
                isSecuredCard = prequalResponse?.ApplicationInformation ? prequalResponse?.ApplicationInformation[0].IsSecuredCard as boolean : false;
            }

            let esignRule: 'Required' | 'Not Shown' | 'Optional';
            const { data: esignContent } = await axios.get<any, { data: IEsignContent }>(`/services/esignContent?programCode=${programCode}`);
            if (isSecuredCard) {
                esignRule = 'Required';
            } else {
                const { data: { ESignRule } } = await acquisitionApiRequest<{ ESignRule: 'Required' | 'Optional' | 'Not Shown' }>('get-esign-rules', {
                    method: 'GET',
                    appendToUrl: `/${programCode}`,
                });
                esignRule = ESignRule;
            }

            if (esignRule === 'Not Shown') {
                setIsEsignRequired(false);
                setIsEsignShowing(false);
                return;
            }

            if ( esignRule === 'Required' ) {
                setIsEsignRequired(true);
                if (!areAllEsignShown(esignContent) && !isAuthorMode() ) {
                    handleRedirect();
                    return;
                }
            } else { // 'Optional'
                setIsEsignRequired(false);
            }
            let esignShowing = true;

            setEsignContent(esignContent);
            setEsignVersion(esignContent.version);

            if (esignRule === 'Optional') {
                esignShowing = areAllEsignShown(esignContent);
            }

            setIsEsignShowing(esignShowing);
        } catch (e) {
            setIsEsignShowing(false);
            console.error(e || 'Unable to retrieve EsignContent');
        }
    };

    useEffect(() => {
        if (isApplicationInfoRecieved) {
            handleEsign();
        }
    }, [isApplicationInfoRecieved]);

    useEffect(() => {
        const submitConsentManagement = async () => {
            const isPHA = getAcquisitionFlow() === AcquisitionFlow.PHA;

            if(isPHA) {
                const flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', null);
                const consent = await submitConsent(flowApplicationData);
                if(consent) {
                    return;
                }

                const origin = window.location.origin;
                const url = new URL(origin + `/pre-qualification/data-entry/index`);
                clearSessionAndRedirectToDataEntryPage('processingError', url.toString());                    
            }
        }
        setIsEsignAgreed(agree);
        SessionStorage.set('isEsign', agree.toString());
    
        if(agree) {
            submitConsentManagement();
        }
    }, [agree]);

    useEffect(() => {
        setIsEsignLinkValid(esignContent?.experienceFragmentExists  ?? false);
    }, [esignContent?.experienceFragmentExists]);

    useEffect(() => {
        if (esignContent) {
            if (!esignContent.experienceFragmentExists && !hasWarned) {
                console.error('Experience fragment cannot be evaluated.');
                setHasWarned(true);
            }
        }
    }, [esignContent?.experienceFragmentExists, hasWarned]);

    const wrapLink = (text: string, link: string) => {
        const regex = /{{(.*?)}}/g;
        return text.replace(regex, `<a href="${link}" target="_blank">$1</a>`);
    };

    useEffect(() => {
        setInstructionText(wrapLink(esignContent?.instructionText ?? '', esignContent?.disclosureText ?? ''));
    }, [esignContent]);

    useEffect(() => {
        getAppData().then(appData => {
            setAgree(appData.ApplicantESignInterest === 1);
        });
    }, []);

    return (
        !isProduction || isEsignShowing || (isEsignRequired && esignContent) ?
            <Container id='application-esign' className='ApplicationEsign'>
                {props.topBorderAdded && <hr className='ApplicationEsign__hr no-print' />}
                <Row className='px-4'>
                    <Col className='ApplicationEsign__titleContainer balance-transfer-header no-print' xs={12}>
                        <h3 className='ApplicationEsign__title'>{transformHTMLParserLinks(esignContent?.fieldsetTitle ??  '')}</h3>
                    </Col>
                    <Col className='ApplicationEsign__instructionalText no-print'
                        xs={12}>{transformHTMLParserLinks(instructionText)}</Col>
                    <Col className='ApplicationEsign__agreeText no-print mt-4'>
                        <form name='Esign'>
                            <InputCheckbox id='EsignID esign-checkbox' name='Esign'
                                label={transformHTMLParserLinks(esignContent?.agreeText ?? '')} value={agree}
                                setValue={setAgree}
                                labelClass='ApplicationEsign__agreeLabel'
                                checkboxId='esign-inputcheckbox'
                            />
                        </form>
                    </Col>
                </Row>
                {props.bottomBorderAdded && <hr className='ApplicationEsign__hr no-print' />}
            </Container>
            : <></>
    );
};


export default ApplicationEsign;
